import { Select, Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';

import type { CheckoutsVerifyIntentResponse } from 'src/api/endpoints/storeApi.types';
import type { CountryData } from 'src/utils/countryList';
import { CA_PROVINCES, US_STATES } from 'src/utils/countryList';
import { formatAmount } from 'src/utils/productUtils';

interface ConnectSummaryProps {
  data: CheckoutsVerifyIntentResponse;
  country?: CountryData;
  state?: string;
}

export const ConnectSummary = ({
  data,
  country,
  state,
}: ConnectSummaryProps) => {
  const { i18n } = useLingui();
  const priceWithTax = formatAmount(
    data.data.cartPrice.total,
    data.data.currency
  );
  const priceWithoutTax = formatAmount(
    data.data.cartPrice.base,
    data.data.currency
  );
  const tax = formatAmount(data.data.cartPrice.tax, data.data.currency);

  const includesTax = data.data.tax.isIncluded;

  const isUs = country?.value === 'US';
  const isCanada = country?.value === 'CA';

  const stateLabel =
    isUs && state
      ? US_STATES.find((s) => s.value === state)?.label
      : isCanada && state
        ? CA_PROVINCES.find((s) => s.value === state)?.label
        : state;

  const countryLabel = isUs && stateLabel ? 'US' : (country?.label ?? '');

  const taxLocation = new Intl.ListFormat(i18n.locale, {
    type: 'unit',
  }).format([stateLabel, countryLabel].filter(Boolean) as string[]);

  return (
    <>
      <div
        className="flex flex-row items-center justify-between font-semibold"
        data-cy="connect-plan-row-price"
      >
        <Typography variant="heading-subtle-2xl">
          <Trans>Connect subscription</Trans>
        </Typography>
        <Typography
          variant="interface-xl"
          fontWeight="semibold"
          leadingNone
          data-cy="connect-plan-row-price-amount"
        >
          {includesTax ? priceWithTax : priceWithoutTax}
        </Typography>
      </div>
      <div className="flex flex-row items-center justify-between">
        <p className="m-0 text-16">
          <Select
            context="Pricing"
            comment="Label for tax value."
            value={isUs ? 'isUsTax' : 'isNotUsTax'}
            _isUsTax={<Trans>Sales tax to {taxLocation}</Trans>}
            other={<Trans>VAT to {taxLocation}</Trans>}
          />
        </p>
        <p className="m-0 text-16">
          {includesTax ? (
            <Trans
              context="Tax"
              comment="Whether tax is included in the price and not listed separately."
            >
              included
            </Trans>
          ) : (
            tax
          )}
        </p>
      </div>
      {!includesTax && (
        <div className="flex flex-row items-center justify-between font-semibold">
          <p className="m-0 text-16">
            <Trans
              context="Pricing"
              comment="Label for the full price, including tax or VAT."
            >
              Total
            </Trans>
          </p>
          <p data-cy="connect-total-price" className="m-0 text-16">
            {priceWithTax}
          </p>
        </div>
      )}
    </>
  );
};
